/**
 * ─────────────────────────────────────────────
 * src/services/meetingMinuteService.js
 * ─────────────────────────────────────────────
 * The Meeting Minutes tab on a project.
 *
 *   GET    /projects/:projectId/minutes            list, newest first
 *   GET    /projects/:projectId/minutes/:minuteId  one meeting in full
 *   POST   /projects/:projectId/minutes            record a meeting
 *   PUT    /projects/:projectId/minutes/:minuteId  edit one
 *   DELETE /projects/:projectId/minutes/:minuteId  remove one
 *
 * Paths are written WITHOUT the "/api" prefix — the shared axios instance
 * supplies it via baseURL.
 *
 * The form and the cards in Projects.js are the reference for shape, so the
 * two mappers below sit here rather than in the page: the server speaks
 * `meetingAt` / `attendees[{user,isPresent}]` / `decisions[{text}]`, the form
 * speaks `date` + `time` / `attendeeIds` / plain decision strings. Keeping
 * both translations in one file means the page never has to know.
 */
import api from "../lib/api";

// ─────────────────────────────────────────────
// FORM → SERVER
//
// `date` and `time` are two inputs in the form but one Date on the server.
// They are combined as a local datetime (no trailing "Z") so the meeting is
// stored at the wall-clock time the user typed, in their own timezone.
//
// Attendees: the form tracks only the ids it has ticked, but an unticked
// resource is a meaningful record ("invited, did not attend"), so every
// project resource is sent with its own isPresent flag.
// ─────────────────────────────────────────────
const toServerPayload = (draft, { decisions = [], memberIds = [] } = {}) => ({
  title: (draft.title || "").trim(),
  meetingType: draft.type,
  meetingAt: draft.date && draft.time ? `${draft.date}T${draft.time}` : null,
  taskId: draft.taskId || null,
  attendees: memberIds.map((id) => ({
    user: id,
    isPresent: (draft.attendeeIds || []).includes(id),
  })),
  notes: (draft.notes || "").trim(),
  decisions: decisions.map((text) => String(text).trim()).filter(Boolean),
});

// ─────────────────────────────────────────────
// SERVER → CARD
//
// Returns exactly the keys the existing cards already read — `id`, `date`,
// `time`, `type`, `taskName`, `attendees[].name`, `decisions` as plain
// strings — so the rendering in Projects.js needs no change at all.
//
// `_id` is kept alongside `id` for later edit/delete calls.
// ─────────────────────────────────────────────
const fromServerMinute = (minute) => {
  const when = minute.meetingAt ? new Date(minute.meetingAt) : null;
  const pad = (n) => String(n).padStart(2, "0");

  return {
    id: minute._id,
    _id: minute._id,
    title: minute.title || "",
    // The card prints `date` verbatim, so format it for reading rather than
    // leaving it as an ISO string.
    date: when
      ? when.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "",
    time: when ? `${pad(when.getHours())}:${pad(when.getMinutes())}` : "",
    // The edit form's <input type="date"> needs YYYY-MM-DD, which `date`
    // above is not (it is formatted for reading). Built from local parts, not
    // toISOString(), so a late-evening meeting keeps its own calendar day.
    dateInput: when
      ? `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`
      : "",
    type: minute.meetingType || "",
    taskId: minute.taskId || "",
    taskName: minute.taskName || "No task selected",
    // Only the people who actually attended are listed on the card.
    attendees: (minute.attendees || [])
      .filter((a) => a.isPresent)
      .map((a) => ({ _id: a._id, name: a.name })),
    // The ids the edit form should show as ticked. Kept separate from
    // `attendees` above because the card lists names while the form needs the
    // ids, and an absent attendee must stay unticked rather than disappear.
    attendeeIds: (minute.attendees || [])
      .filter((a) => a.isPresent && a._id)
      .map((a) => a._id),
    // Detail responses carry the arrays; list responses carry only counts.
    decisions: (minute.decisions || []).map((d) =>
      typeof d === "string" ? d : d.text,
    ),
    decisionCount: minute.decisionCount ?? (minute.decisions || []).length,
    notes: minute.notes || "",
    attachments: (minute.attachments || []).map((file) => ({
      name: file.name,
      type: file.fileType === "image" ? "image/*" : file.fileType,
      url: file.url,
    })),
    // "Raise a change from this meeting" is the next step — the cards read
    // this key, so an empty list keeps them rendering as they do today.
    changeRequests: [],
  };
};

// ─────────────────────────────────────────────
// LIST MINUTES FOR A PROJECT
// ─────────────────────────────────────────────
const getMinutes = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/minutes`);
  return response.data;
};

// ─────────────────────────────────────────────
// ONE MEETING IN FULL (decisions, notes, attachments)
// ─────────────────────────────────────────────
const getMinuteById = async (projectId, minuteId) => {
  const response = await api.get(`/projects/${projectId}/minutes/${minuteId}`);
  return response.data;
};

// ─────────────────────────────────────────────
// MEETING TYPES for the dropdown
// ─────────────────────────────────────────────
const getMinuteOptions = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/minutes/options`);
  return response.data;
};

// ─────────────────────────────────────────────
// CHANGE REQUESTS ALREADY RAISED FROM ONE MEETING
// Used when editing, to show which changes already have a row so saving
// again does not raise them a second time.
// ─────────────────────────────────────────────
const getMinuteChangeRequests = async (projectId, minuteId) => {
  const response = await api.get(
    `/projects/${projectId}/minutes/${minuteId}/change-requests`,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// DRAFT NOTES + DECISIONS FROM A TRANSCRIPT
//
// Returns { notes, decisions[] } and saves nothing — the form fills itself
// in and the user edits and saves as normal.
//
// The per-request timeout overrides the instance default (30s): a long
// transcript genuinely takes longer than that to process, and a timeout here
// would look like a broken feature rather than a slow one.
// ─────────────────────────────────────────────
const draftFromTranscript = async (projectId, transcript) => {
  const response = await api.post(
    `/projects/${projectId}/minutes/draft`,
    { transcript },
    { timeout: 130000 },
  );
  return response.data;
};

// ─────────────────────────────────────────────
// SAME, FROM AN UPLOADED DOCUMENT (.docx or .txt)
//
// The file is read for its text on the server and discarded — it is never
// stored. Content-Type is left undefined so axios derives the multipart
// boundary from the FormData itself.
// ─────────────────────────────────────────────
const draftFromTranscriptFile = async (projectId, file) => {
  const form = new FormData();
  form.append("file", file);
  const response = await api.post(
    `/projects/${projectId}/minutes/draft`,
    form,
    { timeout: 130000, headers: { "Content-Type": undefined } },
  );
  return response.data;
};

// ─────────────────────────────────────────────
// APPROVE OR REJECT MINUTES A CLIENT SUBMITTED
//
// Admin and manager only. Approving also releases any change requests
// raised from the same meeting — they were submitted together.
// ─────────────────────────────────────────────
const reviewMinute = async (projectId, minuteId, decision, note) => {
  const response = await api.patch(
    `/projects/${projectId}/minutes/${minuteId}/approval`,
    { decision, note },
  );
  return response.data;
};

// ─────────────────────────────────────────────
// CREATE
// ─────────────────────────────────────────────
const createMinute = async (projectId, payload) => {
  const response = await api.post(`/projects/${projectId}/minutes`, payload);
  return response.data;
};

// ─────────────────────────────────────────────
// UPDATE — only the fields supplied are changed
// ─────────────────────────────────────────────
const updateMinute = async (projectId, minuteId, payload) => {
  const response = await api.put(
    `/projects/${projectId}/minutes/${minuteId}`,
    payload,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// DELETE
// ─────────────────────────────────────────────
const deleteMinute = async (projectId, minuteId) => {
  const response = await api.delete(
    `/projects/${projectId}/minutes/${minuteId}`,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// UPLOAD ATTACHMENTS to a saved meeting
// Multipart field name is "files" — it must match minuteUpload.array("files")
// on the route, or multer rejects the request as an unexpected field.
// ─────────────────────────────────────────────
const uploadMinuteAttachments = async (projectId, minuteId, files) => {
  const form = new FormData();
  files.forEach((file) => form.append("files", file));
  const response = await api.post(
    `/projects/${projectId}/minutes/${minuteId}/attachments`,
    form,
    // The shared instance defaults to application/json. Setting the multipart
    // type by hand would omit the boundary and multer would see no files, so
    // undefined is passed instead: axios then derives the full header,
    // boundary included, from the FormData body.
    { headers: { "Content-Type": undefined } },
  );
  return response.data;
};

const meetingMinuteService = {
  getMinutes,
  getMinuteById,
  getMinuteOptions,
  getMinuteChangeRequests,
  draftFromTranscript,
  draftFromTranscriptFile,
  createMinute,
  reviewMinute,
  updateMinute,
  deleteMinute,
  uploadMinuteAttachments,
  toServerPayload,
  fromServerMinute,
};

export default meetingMinuteService;
